import { IconCopy } from '@tabler/icons-react';
import { Highlight, type Language } from 'prism-react-renderer';
import { cn } from './styles';
import type { Citation } from '../services/types';
import { CitationMarker } from '../system-message/CitationMarker';
import { usePlaygroundCtaInterceptor } from '../hooks/usePlaygroundCtaInterceptor';

// Routes inline markdown links through the same playground interceptor as structured CTA buttons.
const PlaygroundAwareLink = ({
  href,
  children,
  onClick,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const { interceptCtaClick } = usePlaygroundCtaInterceptor();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (interceptCtaClick(href)) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <a {...rest} href={href} onClick={handleClick}>
      {children}
    </a>
  );
};

export const markdownOverrides = Object.freeze({
  a: {
    component: PlaygroundAwareLink,
  },
  code: {
    component: ({
      className,
      children,
    }: {
      className?: string;
      children?: React.ReactNode;
    }) => {
      const code = String(children ?? '').trim();
      const language: Language =
        (className || '').replace('lang-', '').replace('language-', '') ||
        'javascript';

      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(code);
        } catch {
          /* ignore */
        }
      };

      return (
        <div className="caddietw-relative caddietw-w-full caddietw-max-w-full">
          <button
            onClick={handleCopy}
            className="caddietw-absolute caddietw-top-2 caddietw-right-2 caddietw-z-10 caddietw-flex caddietw-items-center caddietw-gap-1 caddietw-text-xs caddietw-bg-black caddietw-text-white caddietw-rounded caddietw-px-2 caddietw-py-1 hover:caddietw-bg-black/80"
            aria-label="Copy code"
          >
            <IconCopy size={14} /> Copy
          </button>
          <Highlight code={code} language={language}>
            {(renderProps: unknown) => {
              const {
                className: cnClass,
                style,
                tokens,
                getLineProps,
                getTokenProps,
              } = renderProps as unknown as {
                className: string;
                style: React.CSSProperties;
                tokens: Array<Array<{ types: string[]; content: string }>>;
                getLineProps: (args: {
                  line: { types: string[]; content: string }[];
                }) => Record<string, unknown>;
                getTokenProps: (args: {
                  token: { types: string[]; content: string };
                }) => Record<string, unknown>;
              };
              return (
                <pre
                  className={cn(
                    cnClass,
                    'caddietw-rounded-lg caddietw-text-xs caddietw-overflow-auto caddietw-p-4 caddietw-w-full caddietw-max-w-full'
                  )}
                  style={style}
                >
                  {tokens.map((line, i: number) => (
                    <div key={i} {...getLineProps({ line })}>
                      {line.map((token, key: number) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </pre>
              );
            }}
          </Highlight>
        </div>
      );
    },
  },
});

// markdown-to-jsx only intercepts HTML elements, so [n] markers are
// rewritten to <cite> here, before parsing.

// Code spans/fences, skipped so code like arr[0] isn't read as a citation.
const CODE_REGION_RE = /(`{3,}|~{3,})[\s\S]*?\1|(`+)[\s\S]*?\2/g;

export function preprocessCitationMarkers(text: string): string {
  const seen = new Set<string>();
  const replaceCitations = (segment: string): string =>
    segment.replace(/\s*\[\d+\](?!\()(?:\s*\[\d+\](?!\())*/g, (run: string) => {
      const leadingWhitespace = run.match(/^\s+/)?.[0] ?? '';
      const indices: string[] = [];
      for (const m of run.matchAll(/\[(\d+)\]/g)) {
        const index = m[1];
        // LLM over-cites; keep only the first occurrence, text-wide.
        if (seen.has(index)) continue;
        seen.add(index);
        indices.push(index);
      }
      if (indices.length === 0) return '';
      return `${leadingWhitespace}<cite>[${indices.join(',')}]</cite>`;
    });

  let result = '';
  let lastIndex = 0;
  for (const match of text.matchAll(CODE_REGION_RE)) {
    const start = match.index ?? 0;
    result += replaceCitations(text.slice(lastIndex, start));
    result += match[0];
    lastIndex = start + match[0].length;
  }
  result += replaceCitations(text.slice(lastIndex));
  return result;
}

// null until citation metadata streams in, so chips never flash empty.
export function createMarkdownOverrides(citations?: Citation[]) {
  return {
    ...markdownOverrides,
    cite: {
      component: ({ children }: { children?: React.ReactNode }) => {
        const text = String(children ?? '');
        // Token shape ([1,2,3]) is guaranteed by preprocessCitationMarkers, the sole <cite> producer.
        const indices = [...text.matchAll(/\d+/g)].map((m) => Number(m[0]));
        if (indices.length === 0) return <>{children}</>;

        const resolved = indices
          .map((i) => citations?.find((c) => c.index === i))
          .filter(
            (c): c is Citation => !!c && !!c.url && /^https?:\/\//.test(c.url)
          );
        if (resolved.length === 0) return null;

        return (
          <CitationMarker
            citations={resolved}
            animationClass="caddie-citation-enter"
          />
        );
      },
    },
  };
}
